"use client"

import React, { useEffect, useState } from "react"
import { Save, RefreshCw, Building2 } from "lucide-react"
import { useSnackbar } from "notistack"
import { useApi } from "../../hooks/Apis"
import utils from "../../utils"

interface AppConfig {
  dateFormat: string
  hideProfit: boolean
  sendBackup: boolean
  showGallery: boolean
  defaultSortOrder: string
  phoneNumber: string
  pinNumber: string
  vatNumber: string
  branchName: string
  poBox: string
  businessName: string
}

interface OrgData {
  clientorganizationid: number
  name: string
  appconfig: AppConfig
}

const DATE_FORMATS = [
  { label: "DD-MM-YYYY (e.g. 18-02-2026)", value: "DD-MM-YYYY" },
  { label: "MM-DD-YYYY (e.g. 02-18-2026)", value: "MM-DD-YYYY" },
  { label: "YYYY-MM-DD (e.g. 2026-02-18)", value: "YYYY-MM-DD" },
  { label: "DD/MM/YYYY (e.g. 18/02/2026)", value: "DD/MM/YYYY" },
  { label: "MM/DD/YYYY (e.g. 02/18/2026)", value: "MM/DD/YYYY" },
  { label: "D MMM YYYY (e.g. 18 Feb 2026)", value: "D MMM YYYY" },
]

const SORT_ORDERS = [
  { label: "Today only", value: "today" },
  { label: "Last 7 Days", value: "last7" },
  { label: "Last 30 Days", value: "last30" },
  { label: "Last 6 Months", value: "last6months" },
  { label: "Last Year", value: "lastyear" },
]

const BOOLEAN_SETTINGS: { key: keyof AppConfig; label: string; description: string }[] = [
  { key: "hideProfit", label: "Hide Profit", description: "Hide profit column from the sales table" },
  { key: "sendBackup", label: "Send Backup", description: "Automatically send data backup emails" },
  { key: "showGallery", label: "Show Gallery", description: "Enable product image gallery view" },
]

const defaultAppConfig: AppConfig = {
  dateFormat: "DD-MM-YYYY",
  hideProfit: false,
  sendBackup: false,
  showGallery: false,
  defaultSortOrder: "last7",
  phoneNumber: "",
  pinNumber: "",
  vatNumber: "",
  branchName: "",
  poBox: "",
  businessName: "",
}

const Settings: React.FC = () => {
  const { apiRequest } = useApi()
  const { enqueueSnackbar } = useSnackbar()
  const [orgData, setOrgData] = useState<OrgData | null>(null)
  const [orgName, setOrgName] = useState("")
  const [appConfig, setAppConfig] = useState<AppConfig>(defaultAppConfig)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  const loadFromLocalStorage = () => {
    const clientUser = localStorage.getItem("clientuser")
      ? JSON.parse(localStorage.getItem("clientuser") || "{}")
      : null
    return clientUser?.clientData || null
  }

  const fetchOrgData = async () => {
    setIsLoading(true)
    try {
      const clientorganizationid = localStorage.getItem("clientorganizationid")
      const response = await apiRequest({
        method: "GET",
        url: `${utils.baseUrl}/api/clientorganizations/list`,
      })
      const orgs: OrgData[] = response?.data || []
      const org = orgs.find((o) => String(o.clientorganizationid) === String(clientorganizationid))

      if (org) {
        setOrgData(org)
        setOrgName(org.name)
        setAppConfig({ ...defaultAppConfig, ...(org.appconfig || {}) })
      } else {
        // Fallback to localStorage
        const localData = loadFromLocalStorage()
        if (localData) {
          setOrgName(localData.name || "")
          setAppConfig({ ...defaultAppConfig, ...(localData.appconfig || {}) })
        }
      }
    } catch {
      // Fallback to localStorage on error
      const localData = loadFromLocalStorage()
      if (localData) {
        setOrgName(localData.name || "")
        setAppConfig({ ...defaultAppConfig, ...(localData.appconfig || {}) })
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchOrgData()
  }, [])

  const handleBooleanToggle = (key: keyof AppConfig) => {
    setAppConfig((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
        const response = await apiRequest({
        method: "POST",
        url: `${utils.baseUrl}/api/clientorganizations/update/${orgData?.clientorganizationid}`,
        data: { 
            name: orgName, 
            appconfig: JSON.stringify(appConfig),
            clientorganizationid: orgData?.clientorganizationid
        },
        })

      if (response?.success) {
        // Update localStorage to keep it in sync
        const clientUser = localStorage.getItem("clientuser")
          ? JSON.parse(localStorage.getItem("clientuser") || "{}")
          : {}
        clientUser.clientData = { ...clientUser.clientData, name: orgName, appconfig: appConfig }
        localStorage.setItem("clientuser", JSON.stringify(clientUser))

        enqueueSnackbar("Settings saved successfully", { variant: "success" })
      } else {
        enqueueSnackbar("Failed to save settings", { variant: "error" })
      }
    } catch {
      enqueueSnackbar("Failed to save settings. Please try again.", { variant: "error" })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>

      <div className="space-y-6">

        {/* Organization Info */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <Building2 size={20} className="text-purple-600" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Organization</h2>
          </div>
          <div className="px-6 py-5">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Organization Name
            </label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div className="px-6 pb-5">
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Business Name <span className="text-xs text-gray-400">(shown on receipt footer)</span>
        </label>
        <input
            type="text"
            placeholder="e.g. HAWKERS PRIDE"
            value={appConfig.businessName || ""}
            onChange={(e) => setAppConfig((prev) => ({ ...prev, businessName: e.target.value }))}
            className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
        </div>

        {/* Business Details */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Business Details</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Shown on printed receipts</p>
        </div>
        <div className="grid grid-cols-1 gap-5 px-6 py-5 sm:grid-cols-3">
            {[
            { key: "phoneNumber", label: "Phone Number", placeholder: "e.g. 0715346417" },
            { key: "pinNumber", label: "PIN Number", placeholder: "e.g. P051116421N" },
            { key: "vatNumber", label: "VAT Number", placeholder: "e.g. 0416276S" },
            { key: "branchName", label: "Branch Name", placeholder: "e.g. MAIN" },
            { key: "poBox", label: "P.O. Box", placeholder: "e.g. P.O. BOX 8545 NAIROBI" },
            ].map(({ key, label, placeholder }) => (
            <div key={key}>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {label}
                </label>
                <input
                type="text"
                placeholder={placeholder}
                value={(appConfig as any)[key] || ""}
                onChange={(e) => setAppConfig((prev) => ({ ...prev, [key]: e.target.value }))}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
            </div>
            ))}
        </div>
        </div>

        {/* Display Settings */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Display Settings</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Configure how data is displayed</p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">

            {/* Date Format */}
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Date Format</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Format used to display dates across the app</p>
              </div>
              <select
                value={appConfig.dateFormat}
                onChange={(e) => setAppConfig((prev) => ({ ...prev, dateFormat: e.target.value }))}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {DATE_FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Default Sort Order */}
            <div className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Default Date Filter</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Default date range shown on Sales screen</p>
              </div>
              <select
                value={appConfig.defaultSortOrder}
                onChange={(e) => setAppConfig((prev) => ({ ...prev, defaultSortOrder: e.target.value }))}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {SORT_ORDERS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Feature Toggles</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Enable or disable specific features</p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {BOOLEAN_SETTINGS.map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
                </div>
                <button
                  onClick={() => handleBooleanToggle(key)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    appConfig[key] ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      appConfig[key] ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Save size={16} />
            )}
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Settings